'use client';

import { ApiKeys, fetcher } from '@insta-club/api';
import { CalenderIcon, InfoIcon, LoadingIcon } from '@insta-club/components';
import { Calendar, Note, Program } from '@insta-club/models';
import { useRequireSubscriptionSheetStore, useSessionStore } from '@insta-club/state';
import { CircularProgress } from '@insta-club/ui';
import {
    cn,
    getReadableJalaaliFromBaseDate,
    isBeforeNowJalaali,
    isSameJalaaliDate,
    toOrdinalPersian
} from '@insta-club/utilities';

import { useEffect, useMemo } from 'react';
import useSWR from 'swr';
import RequireSubscription from '../RequireSubscription';
import AlgorithmicTask from './algorithmic';
import PostingTask from './posting';
import StoryTask from './story';

interface Props {
    id: number;
    calendar: Calendar;
    onNotes?: (notes: Note[]) => void;
}

const fetchProgram = ([url, day]: [string, number]) => fetcher(url, 'dashboard', 'GET', { params: { day } });

const ProgramDetails: React.FC<Props> = ({ id, calendar, onNotes }) => {
    const requireSubscriptionSheet = useRequireSubscriptionSheetStore((state) => state);
    const hasSubscription = useSessionStore((state) => state.page?.subscription?.active);
    const { data: program, isLoading } = useSWR<Program>(
        hasSubscription ? ApiKeys.GET_PROGRAM_SHOW(id, calendar.day) : null,
        fetchProgram
    );

    const canPerformTask = useMemo(() => {
        if (isSameJalaaliDate(calendar.date)) return true;
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        if (isSameJalaaliDate(calendar.date, yesterday)) return true;
        return false;
    }, [calendar.date]);

    useEffect(() => {
        if (program?.notes && onNotes) {
            onNotes(program.notes);
        }
    }, [program, onNotes]);

    return (
        <>
            <p className='text-primary-900 flex items-center gap-1 text-base font-medium'>
                <CalenderIcon width={20} height={20} />
                <span className='space-x-0.5 font-semibold'>
                    <span>برنامه</span>{' '}
                    {isSameJalaaliDate(calendar.date) ? (
                        <>امروز</>
                    ) : (
                        <>
                            {getReadableJalaaliFromBaseDate(calendar.date)}{' '}
                            <span className='font-normal text-gray-500'>{`( روز ${toOrdinalPersian(calendar.day)} )`}</span>
                        </>
                    )}
                </span>
            </p>

            {program?.progress && (isSameJalaaliDate(calendar.date) || isBeforeNowJalaali(calendar.date)) && (
                <Progress percentage={Number(program.progress)} canPerformTask={isSameJalaaliDate(calendar.date)} />
            )}

            <div className='relative' onClick={!hasSubscription ? requireSubscriptionSheet.openDrawer : undefined}>
                <div
                    className={cn(
                        'absolute inset-0 flex items-center justify-center rounded-2xl bg-white/10 backdrop-blur-xs',
                        {
                            'pointer-events-auto z-50 opacity-100': isLoading,
                            'pointer-events-none z-0 opacity-0': !isLoading
                        }
                    )}
                >
                    <LoadingIcon width={40} height={40} className='text-gray-500' />
                </div>
                <div className='space-y-4'>
                    <PostingTask
                        post={program?.post}
                        hasChatBot={!!program?.has_post_chat_bot}
                        programID={id}
                        dayNumber={calendar.day}
                        canPerformTask={canPerformTask}
                    />
                    <StoryTask
                        hasChatBot={!!program?.has_story_chat_bot}
                        story={program?.story}
                        programID={id}
                        dayNumber={calendar.day}
                        date={`${calendar?.date?.year}-${calendar?.date?.month}-${calendar?.date?.day}`}
                        canPerformTask={canPerformTask}
                    />
                    <AlgorithmicTask
                        programID={id}
                        dayNumber={calendar.day}
                        algorithms={program?.algoritms}
                        canPerformTask={canPerformTask}
                    />
                    {!isSameJalaaliDate(calendar.date) && (
                        <div className='flex items-start space-x-2'>
                            <InfoIcon className='text-primary-700' />
                            <span className='text-sm text-gray-500'>
                                وضعیت تسک‌های برنامه تنها در همان روز یا روز بعد قابل به‌روزرسانی است.
                            </span>
                        </div>
                    )}
                </div>
            </div>

            <RequireSubscription />
        </>
    );
};

interface ProgressProps {
    percentage: number;
    canPerformTask: boolean;
}

const Progress: React.FC<ProgressProps> = ({ percentage, canPerformTask }) => {
    return (
        <div className='flex items-center gap-2'>
            <CircularProgress
                barColor={percentage == 100 ? 'text-green-500' : 'text-primary-500'}
                size={24}
                strokeWidth={5}
                showText={false}
                percentage={percentage}
            />
            <p className='text-right text-sm font-medium text-gray-700'>
                {!canPerformTask
                    ? `${percentage}% از برنامه رو انجام دادی`
                    : percentage === 0
                      ? 'تا موفقیت دقیق و منظم باش🏅و برنامه امروزتو به بهترین شکل شروع کن'
                      : percentage < 50
                        ? 'خوب داری پیش میری 👏 ادامه بده!'
                        : percentage < 100
                          ? 'نصف راهو اومدی 💪 فقط یه کم دیگه مونده!'
                          : 'دمت گرم! همه‌ی تسک‌هاتو با موفقیت تموم کردی 🎉🔥'}
            </p>
        </div>
    );
};

export default ProgramDetails;

